import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Flame, Zap, Activity, Radio, Globe2, Flag, Users, Trophy, Swords, Share2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { getLivePulseSnapshot } from "@/lib/social.functions";
import { AnimatedNumber, PulseDeltaBadge } from "./PulseDeltaBadge";

type WorldPulseRow = {
  id: number;
  temperature: number;
  hype: number;
  energy: number;
  momentum: number;
  updated_at: string;
};

const FALLBACK: WorldPulseRow = {
  id: 1, temperature: 50, hype: 0, energy: 0, momentum: 0, updated_at: new Date().toISOString(),
};

/**
 * World Pulse Live — cinematic KPI strip fed by `world_pulse` (Supabase Realtime).
 * Reuses `getLivePulseSnapshot` for active fans.
 */
export function WorldPulseLive() {
  const [pulse, setPulse] = useState<WorldPulseRow>(FALLBACK);
  const [pulseTick, setPulseTick] = useState(0);

  useEffect(() => {
    let alive = true;
    supabase
      .from("world_pulse")
      .select("id, temperature, hype, energy, momentum, updated_at")
      .eq("id", 1)
      .maybeSingle()
      .then(({ data }) => { if (alive && data) setPulse(data as WorldPulseRow); });

    const channel = supabase
      .channel("world_pulse_live")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "world_pulse" },
        (payload) => {
          const row = (payload.new ?? payload.old) as WorldPulseRow | null;
          if (row && row.id === 1) {
            setPulse(row);
            setPulseTick((t) => t + 1);
          }
        },
      )
      .subscribe();

    return () => { alive = false; supabase.removeChannel(channel); };
  }, []);

  const fans = useQuery({
    queryKey: ["worldPulse", "active_fans"],
    queryFn: () => getLivePulseSnapshot(),
    refetchInterval: 15_000,
  });
  const activeFans = fans.data?.active_fans ?? 0;

  // ---- Pre-Mundial counters (real data, count-only queries) ----
  const preCounts = useQuery({
    queryKey: ["worldPulse", "preMundialCounts"],
    queryFn: async () => {
      const safe = async (p: PromiseLike<{ count: number | null }>) => {
        try { const { count } = await p; return count ?? 0; } catch { return 0; }
      };
      const [nations, fansCount, predictions, wars, shares] = await Promise.all([
        safe(supabase.from("worldcup_teams").select("*", { count: "exact", head: true })),
        safe(supabase.from("profiles").select("*", { count: "exact", head: true })),
        safe(supabase.from("prediction_answers").select("*", { count: "exact", head: true })),
        safe(supabase.from("nation_rivalry_stats").select("*", { count: "exact", head: true })),
        safe(supabase.from("victory_card_shares").select("*", { count: "exact", head: true })),
      ]);
      return { nations, fansCount, predictions, wars, shares };
    },
    refetchInterval: 60_000,
  });

  const livePulseTotal =
    Math.round(Number(pulse.hype)) +
    Math.round(Number(pulse.energy)) +
    Math.round(Number(pulse.momentum));
  const preMundial = livePulseTotal === 0 && activeFans === 0;

  const tempColor = useMemo(() => {
    const t = Math.max(0, Math.min(100, Number(pulse.temperature) || 0));
    if (t < 35) return "from-sky-500 to-cyan-400";
    if (t < 60) return "from-amber-500 to-orange-400";
    if (t < 80) return "from-orange-500 to-red-500";
    return "from-red-600 via-pink-500 to-fuchsia-500";
  }, [pulse.temperature]);

  const items: Array<{
    key: string; label: string; value: number; icon: React.ReactNode; tone: string; suffix?: string;
  }> = [
    { key: "temp", label: "Temperatura Mundial", value: Math.round(Number(pulse.temperature)), icon: <Flame className="h-4 w-4" />, tone: tempColor, suffix: "°" },
    { key: "hype", label: "Hype Mundial", value: Math.round(Number(pulse.hype)), icon: <Zap className="h-4 w-4" />, tone: "from-fuchsia-500 to-pink-500" },
    { key: "energy", label: "Energía Global", value: Math.round(Number(pulse.energy)), icon: <Radio className="h-4 w-4" />, tone: "from-emerald-500 to-teal-400" },
    { key: "momentum", label: "Momentum Viral", value: Math.round(Number(pulse.momentum)), icon: <Activity className="h-4 w-4" />, tone: "from-indigo-500 to-violet-500" },
    { key: "fans", label: "Actividad Global", value: activeFans, icon: <Globe2 className="h-4 w-4" />, tone: "from-amber-400 to-yellow-300" },
  ];

  const preItems = [
    { key: "nations", label: "Naciones registradas", value: preCounts.data?.nations ?? 0, icon: <Flag className="h-4 w-4" />, tone: "from-emerald-500 to-teal-400" },
    { key: "fans", label: "Fans registrados", value: preCounts.data?.fansCount ?? 0, icon: <Users className="h-4 w-4" />, tone: "from-amber-400 to-yellow-300" },
    { key: "preds", label: "Predicciones realizadas", value: preCounts.data?.predictions ?? 0, icon: <Trophy className="h-4 w-4" />, tone: "from-fuchsia-500 to-pink-500" },
    { key: "wars", label: "Nation Wars activas", value: preCounts.data?.wars ?? 0, icon: <Swords className="h-4 w-4" />, tone: "from-indigo-500 to-violet-500" },
    { key: "shares", label: "Tarjetas virales", value: preCounts.data?.shares ?? 0, icon: <Share2 className="h-4 w-4" />, tone: "from-sky-500 to-cyan-400" },
  ];

  return (
    <section
      aria-label="World Pulse Live"
      className="relative overflow-hidden rounded-2xl border border-white/10 bg-[radial-gradient(ellipse_at_top,oklch(0.22_0.08_280/_0.6),transparent_60%),linear-gradient(135deg,oklch(0.12_0.02_270),oklch(0.08_0.02_280))] p-4 shadow-[0_20px_60px_-20px_rgba(0,0,0,.6)]"
    >
      <header className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="relative flex h-2 w-2">
            <span className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-75 ${preMundial ? "bg-amber-400" : "bg-red-500"}`} />
            <span className={`relative inline-flex h-2 w-2 rounded-full ${preMundial ? "bg-amber-400" : "bg-red-500"}`} />
          </span>
          <h2 className="text-xs font-black uppercase tracking-[0.18em] text-white/90">
            {preMundial ? "Preparando el Mundial" : "World Pulse · Live"}
          </h2>
        </div>
        <span className="text-[10px] font-medium uppercase tracking-wider text-white/40">
          {preMundial ? "calentando motores" : pulseTick > 0 ? "actualizado ahora" : "en vivo"}
        </span>
      </header>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        {(preMundial ? preItems : items).map((it) => (
          <div
            key={it.key}
            className="group relative overflow-hidden rounded-xl border border-white/10 bg-white/[0.03] p-3 backdrop-blur transition-transform duration-300 hover:-translate-y-0.5"
          >
            <div
              className={`pointer-events-none absolute inset-0 -z-10 bg-gradient-to-br opacity-[0.18] transition-opacity duration-500 group-hover:opacity-30 ${it.tone}`}
            />
            <div className="mb-1 flex items-center gap-1.5 text-white/70">
              {it.icon}
              <span className="text-[10px] font-semibold uppercase tracking-wider">{it.label}</span>
            </div>
            <div className="relative inline-flex items-baseline">
              <AnimatedNumber value={it.value} className="text-2xl font-black leading-none text-white" />
              {"suffix" in it && it.suffix && <span className="ml-0.5 text-sm font-bold text-white/60">{it.suffix}</span>}
              {!preMundial && <PulseDeltaBadge value={it.value} />}
            </div>
          </div>
        ))}
      </div>

      {preMundial && (
        <p className="mt-3 text-center text-[11px] font-medium uppercase tracking-wider text-white/50">
          El pulso global se enciende cuando arranca el Mundial
        </p>
      )}
    </section>
  );
}